import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Grid, Container, Typography, Box, Chip, Divider, Card } from '@mui/material';
import { useEffect, useState } from 'react';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import { useNavigate } from 'react-router-dom';

// ----------------------------------------------------------------------

export default function DetailBeritaPage() {
  const wId = window.location.pathname.split("/", 3)[2];
  const API_URL = process.env.REACT_APP_API;
  const BACKEND_API = process.env.REACT_APP_BE;
  const navigate = useNavigate();
  const [berita, setBerita] = useState([]);
  const [beritaTerbaru, setBeritaTerbaru] = useState([]);

  const getDetailBerita = async () => {
    try {
      await axios.get(`${API_URL}berita/beritaById?id=${wId}`)
        .then(({ data }) => { setBerita(data?.data[0]); })
        .catch((err) => { if (err.response.status === 404) { setBerita([]); } });
    } catch (error) { console.log(error); }
  };

  const getBeritaTerbaru = async () => {
    try {
      await axios.get(`${API_URL}berita/beritaTerbaru`)
        .then(({ data }) => { setBeritaTerbaru(data?.data); })
        .catch((err) => { if (err.response.status === 404) { setBeritaTerbaru([]); } });
    } catch (error) { console.log(error); }
  };

  useEffect(() => {
    getBeritaTerbaru();
    getDetailBerita();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedDate = berita?.tanggal_berita
    ? new Date(berita.tanggal_berita).toLocaleString("id-ID", {
        timeZone: 'UTC',
        weekday: "long",
        month: "long",
        day: "2-digit",
        year: "numeric",
      })
    : '';

  return (
    <>
      <Helmet>
        <title>{`${berita?.judul} | `}{localStorage.getItem("judul") || ''}</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        <meta name="description" content={berita?.content?.replace(/<[^>]*>/g, '')?.slice(0, 200)} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>

      {/* Hero Image */}
      {berita?.image1 && (
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '280px', md: '460px' },
          overflow: 'hidden',
          mt: { xs: '64px', md: '70px' },
        }}>
          <img
            src={`${BACKEND_API}${berita?.image1}`}
            alt={berita?.judul}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {/* Gradient overlay */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(26,26,46,0.85) 0%, rgba(26,26,46,0.3) 50%, transparent 100%)',
          }} />

          {/* Breadcrumb / Back */}
          <Box
            onClick={() => navigate('/berita')}
            sx={{
              position: 'absolute', top: 20, left: { xs: 16, md: 40 },
              display: 'flex', alignItems: 'center', gap: 1,
              color: 'rgba(255,255,255,0.8)',
              cursor: 'pointer',
              background: 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(8px)',
              borderRadius: '50px',
              px: 2, py: 0.8,
              border: '1px solid rgba(255,255,255,0.2)',
              transition: 'all 0.25s ease',
              '&:hover': {
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
              }
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Kembali ke Berita</Typography>
          </Box>

          {/* Title over image */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            px: { xs: 2, md: 8 }, pb: { xs: 3, md: 5 },
          }}>
            <Chip label="Berita" sx={{
              background: 'rgba(184,92,44,0.9)',
              color: '#fff', fontWeight: 700, fontSize: '11px',
              mb: 1.5,
            }} />
            <Typography sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: '20px', md: '36px' },
              lineHeight: 1.25,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              textShadow: '0 2px 12px rgba(0,0,0,0.4)',
              maxWidth: 820,
            }}>
              {berita?.judul}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.65)' }} />
              <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                {formattedDate}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ py: 6, px: { xs: 2, sm: 3, md: 5 } }}>
        <Grid container spacing={4}>

          {/* Main article */}
          <Grid item xs={12} lg={8}>
            <Box sx={{
              background: '#fff',
              borderRadius: '24px',
              p: { xs: 3, md: 5 },
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 30px rgba(0,0,0,0.05)',
            }}>
              {/* Date (if no hero image) */}
              {!berita?.image1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#B85C2C' }} />
                  <Typography sx={{ fontSize: '14px', color: '#6B7280', fontWeight: 500 }}>
                    {formattedDate}
                  </Typography>
                </Box>
              )}
              {!berita?.image1 && (
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: '22px', md: '32px' },
                  color: '#1A1A2E', lineHeight: 1.3,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  mb: 3,
                }}>
                  {berita?.judul}
                </Typography>
              )}

              <Divider sx={{ mb: 3, borderColor: 'rgba(184,92,44,0.12)' }} />

              {/* Article body */}
              <Box sx={{
                color: '#374151',
                lineHeight: 1.9,
                fontSize: '16px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                '& p': { mb: '16px' },
                '& h1, & h2, & h3': {
                  color: '#1A1A2E',
                  fontWeight: 800,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  mt: '24px',
                  mb: '12px',
                },
                '& img': { maxWidth: '100%', borderRadius: '12px', my: '16px' },
                '& strong': { color: '#B85C2C' },
                '& a': { color: '#B85C2C', textDecoration: 'none' },
                '& ul, & ol': { pl: '20px', mb: '16px' },
                '& li': { mb: '8px' },
                '& blockquote': {
                  borderLeft: '4px solid #B85C2C',
                  pl: 2, ml: 0,
                  color: '#6B7280',
                  fontStyle: 'italic',
                },
              }}>
                {/* eslint-disable-next-line */}
                <div dangerouslySetInnerHTML={{ __html: berita?.content }} />
              </Box>
            </Box>
          </Grid>

          {/* Sidebar — Berita Terbaru */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'sticky', top: '90px' }}>
              <Card elevation={0} sx={{
                borderRadius: '20px',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                overflow: 'hidden',
              }}>
                {/* Header */}
                <Box sx={{
                  p: 2.5,
                  background: 'linear-gradient(135deg, #1A1A2E 0%, #2C1810 100%)',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <NewspaperOutlinedIcon sx={{ color: '#F5A623', fontSize: 20 }} />
                  <Typography sx={{
                    color: '#fff', fontWeight: 800, fontSize: '15px',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}>
                    Berita Terbaru
                  </Typography>
                </Box>

                {/* News list */}
                <Box sx={{ p: 2 }}>
                  {beritaTerbaru?.slice(0, 5).map((data, i) => (
                    <Box
                      key={data?.id}
                      onClick={() => navigate(`/berita/${data?.id}`)}
                      sx={{
                        display: 'flex', gap: 2, p: 1.5,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        mb: i < 4 ? 0.5 : 0,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: '#fff8f0',
                        }
                      }}
                    >
                      {/* Thumbnail */}
                      <Box sx={{
                        width: 64, height: 64, borderRadius: '10px',
                        overflow: 'hidden', flexShrink: 0, bgcolor: '#f5f5f5',
                      }}>
                        <img
                          src={`${BACKEND_API}${data?.image1}`}
                          alt={data?.judul}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      {/* Title */}
                      <Box>
                        <Chip label="Berita" size="small" sx={{
                          background: 'rgba(184,92,44,0.1)',
                          color: '#B85C2C',
                          fontSize: '10px', fontWeight: 700,
                          height: 18, mb: 0.5,
                        }} />
                        <Typography sx={{
                          fontSize: '13px', fontWeight: 600,
                          color: '#1A1A2E', lineHeight: 1.35,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {data?.judul}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Footer link */}
                <Box sx={{
                  p: 2, pt: 0, textAlign: 'center',
                }}>
                  <Box
                    onClick={() => navigate('/berita')}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      color: '#B85C2C', fontWeight: 700, fontSize: '13px',
                      cursor: 'pointer',
                      p: '8px 20px',
                      borderRadius: '50px',
                      border: '1.5px solid rgba(184,92,44,0.25)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        background: '#B85C2C',
                        color: '#fff',
                        borderColor: 'transparent',
                      }
                    }}
                  >
                    Lihat Semua Berita
                  </Box>
                </Box>
              </Card>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </>
  );
}
